import DropdownSelectButton, { IDropdownSelectButtonProps } from './dropdownSelectButton';
import { createStyles, makeStyles } from '@mui/styles';
import clsx from 'clsx';
import { FormItemBase, IFormItemBaseProps } from 'copyright-clearance-center-common-ui-react';
import React, { useEffect, useRef, useState } from 'react';

const useStyles = makeStyles(() =>
	createStyles({
		formItemContainer: {
			padding: 0,
			'& > :last-child': {
				flex: 1
			}
		}
	})
);

interface IFormItemWithDropdownProps extends IFormItemBaseProps {
	noForm?: boolean;
	focusableElementSelector?: string;
	dropdownProps: IDropdownSelectButtonProps;
}

const FormItemWithDropdown: React.FC<IFormItemWithDropdownProps> = ({
	noForm,
	focusableElementSelector,
	dropdownProps,
	children,
	...formProps
}) => {
	const classes = useStyles();
	const [inFocus, setInFocus] = useState(false);
	const itemContainerRef = useRef<HTMLDivElement>(null);
	const focusableControl = useRef<HTMLElement>(null);

	useEffect(() => {
		if (!itemContainerRef.current || !focusableElementSelector) {
			return;
		}

		const item = itemContainerRef.current.querySelector(focusableElementSelector);
		if (!item || !(item instanceof HTMLElement)) {
			return;
		}

		focusableControl.current = item;

		const setFocus = () => setInFocus(true);
		const resetFocus = () => setInFocus(false);

		item.addEventListener('focus', setFocus);
		item.addEventListener('blur', resetFocus);

		return () => {
			focusableControl.current = null;
			item.removeEventListener('focus', setFocus);
			item.removeEventListener('blur', resetFocus);
		};
	}, [focusableElementSelector]);

	const renderContent = () => (
		<div
			ref={itemContainerRef}
			className={clsx('rup-input', 'input-wrapper', classes.formItemContainer, {
				'rup-input--focus': inFocus,
				'rup-input--disabled': formProps.disabled
			})}
			onClick={() => focusableControl.current?.focus()}
		>
			<DropdownSelectButton disabled={formProps.disabled} style={{ marginRight: 0 }} {...dropdownProps} />
			{children}
		</div>
	);

	if (noForm) {
		return <div style={formProps.style}>{renderContent()}</div>;
	} else {
		return <FormItemBase {...formProps}>{renderContent()}</FormItemBase>;
	}
};

export default FormItemWithDropdown;
